import { AVAILABLE_REGIONS_LIB_REGIONS } from '~/consts/agentHub';

import { toDayJs } from '~/services/TimeService';

import type { Commission, CreateCommissionParams } from '~/types/services/agentHub';

import { buildCommissionGroupPayload } from '~/utils/agentHub';
import { mergeCommissionAdditionalInfo } from '~/utils/commission';
import { serializeCommissionRuleBoundaryDate } from '~/utils/commissionDates';
import { readCommissionAdditionalInfo } from '~/utils/commissionFlightLocations';

import type { CommissionFormState } from './CommissionDialog.types';
import { VALID_COMMISSION_TYPES } from './CommissionDialog.types';

export type BuildCommissionPayloadResult = { ok: true; payload: CreateCommissionParams } | { ok: false; error: string };

export function buildCommissionPayload(
  value: CommissionFormState,
  existingCommission?: Commission,
): BuildCommissionPayloadResult {
  const commissionGroupPayload = buildCommissionGroupPayload(Object.values(value.commissionGroups));
  if (commissionGroupPayload.length === 0) {
    return { ok: false, error: 'Please add at least one condition' };
  }

  if (!VALID_COMMISSION_TYPES.has(value.commissionType) || !value.commissionType) {
    return { ok: false, error: 'Please select a commission type' };
  }

  let priority: number | null = null;
  if (value.commissionType === 'base') {
    const trimmedPriority = value.priorityInput.trim();
    if (trimmedPriority !== '') {
      const parsed = parseInt(trimmedPriority, 10);
      if (Number.isNaN(parsed) || parsed < 0) {
        return { ok: false, error: 'Priority must be a number greater or equal to 0' };
      }
      priority = parsed;
    }
  }

  const commissionValueLimits: Array<{ region: string; maxAmount: number }> = [];
  if (value.commissionType === 'incentive') {
    const regionsForLimits =
      value.regions.length > 0 ? value.regions : AVAILABLE_REGIONS_LIB_REGIONS.map((region) => region.code);

    for (const region of regionsForLimits) {
      const maxAmountValue = value.commissionValueLimits[region];
      if (maxAmountValue == null || maxAmountValue.trim() === '') {
        continue;
      }

      const parsedMaxAmount = Number(maxAmountValue.trim());
      if (Number.isNaN(parsedMaxAmount) || parsedMaxAmount < 1) {
        return { ok: false, error: 'Commission value limit must be a number greater or equal to 1' };
      }

      commissionValueLimits.push({ region, maxAmount: parsedMaxAmount });
    }
  }

  const additionalInfo = mergeCommissionAdditionalInfo(
    existingCommission ? readCommissionAdditionalInfo(existingCommission) : undefined,
    Object.values(value.commissionGroups).flatMap((group) =>
      group.conditions
        .filter((condition) => condition.type === 'flightDeparture')
        .flatMap((condition) => condition.value),
    ),
    Object.values(value.commissionGroups).flatMap((group) =>
      group.conditions
        .filter((condition) => condition.type === 'flightArrival')
        .flatMap((condition) => condition.value),
    ),
  );

  const commissionPercentage = parseInt(value.commissionPercentage, 10);
  const formObj = {
    rules: {},
    commissionGroups: commissionGroupPayload,
    description: value.description,
    startDate: value.startDate
      ? serializeCommissionRuleBoundaryDate(value.startDate)
      : serializeCommissionRuleBoundaryDate(toDayJs()),
    endDate: value.endDate ? serializeCommissionRuleBoundaryDate(value.endDate) : undefined,
    regions: value.regions,
    discountType: value.commissionType,
    discountAmount: commissionPercentage,
    commissionType: value.commissionType,
    commissionPercentage,
    type: value.commissionType,
    limit: value.shouldIncludeLimit ? parseInt(value.limit, 10) : null,
    commissionValueLimits,
    includesTaxesAndFees: value.includesTaxesAndFees,
    includesMargin: value.includesMargin,
    ...(additionalInfo ? { additionalInfo } : {}),
  };

  const payload =
    value.commissionType === 'base'
      ? { ...formObj, priority, canHaveAdditionalCommissions: value.canHaveAdditionalCommissions }
      : formObj;

  return { ok: true, payload: payload as CreateCommissionParams };
}
